import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Components
import Login from "@/components/Login";
import ResetPassword from "@/components/ResetPassword";
import Dashboard from "@/components/Dashboard";
import ClientsPage from "@/components/ClientsPage";
import SimulatorPage from "@/components/SimulatorPageEnhanced";
import ContractsPage from "@/components/ContractsPage";
import DevisFacturesPage from "@/components/DevisFacturesPage";
import SettingsPage from "@/components/SettingsPage";
import TreasuryPage from "@/components/TreasuryPage";
import ProspectsPage from "@/components/ProspectsPage";
import PlanningPage from "@/components/PlanningPage";
import DistributionRdvPage from "@/components/DistributionRdvPage";
import StatistiquesPage from "@/components/StatistiquesPage";
import AuditLogsPage from "@/components/AuditLogsPage";
import SignatureBDCPage from "@/components/SignatureBDCPage";
import MyCommissionsPage from "@/components/MyCommissionsPage";
import Sidebar from "@/components/Sidebar";
// SUNLIFE - Nouveaux composants
import ProjetsPage from "@/components/ProjetsPage";
import ProduitsPage from "@/components/ProduitsPage";
import SAVPage from "@/components/SAVPage";
import CreneauxOptimisesPage from "@/components/CreneauxOptimisesPage";
import DossiersAidesPage from "@/components/DossiersAidesPage";
import StockPage from "@/components/StockPage";
import TeamManagement from "@/components/TeamManagement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = createContext();

// Setup axios interceptor for 401 errors (token expiration)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      delete axios.defaults.headers.common["Authorization"];
      toast.error("Votre session a expiré. Veuillez vous reconnecter.");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }

    // Precise watermark removal - only target exact watermark patterns
    const removeWatermark = () => {
      // Very specific selectors for watermarks only
      const elements = document.querySelectorAll('div, span, a, p');
      
      elements.forEach(el => {
        const text = el.textContent?.trim() || '';
        const href = el.href || '';
        
        // Only target exact watermark patterns
        const isExactWatermark = (
          text === 'Made with Emergent' ||
          text === 'Powered by Emergent' ||
          text === 'Made with emergent' ||
          text === 'Emergent' ||
          href.includes('emergent.ai') ||
          href.includes('emergentagent.com')
        );
        
        if (isExactWatermark) {
          // Double-check: ensure it's not navigation or important content
          const isNavigationElement = (
            el.closest('nav') ||
            el.closest('.sidebar') ||
            el.closest('[role="navigation"]') ||
            el.querySelector('nav') ||
            text.includes('Dashboard') ||
            text.includes('Client') ||
            text.includes('Contrat') ||
            el.tagName === 'NAV'
          );
          
          if (!isNavigationElement) {
            // Check position - watermarks are usually small and in corners
            const rect = el.getBoundingClientRect();
            const isLikelyWatermark = (
              rect.width < 300 && rect.height < 60 &&
              (
                // Bottom right corner
                (rect.bottom > window.innerHeight - 100 && rect.right > window.innerWidth - 300) ||
                // Bottom left corner  
                (rect.bottom > window.innerHeight - 100 && rect.left < 300) ||
                // Small size anywhere
                (rect.width < 200 && rect.height < 40)
              )
            );
            
            if (isLikelyWatermark) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.opacity = '0';
            }
          }
        }
      });
    };

    // Run immediately and more frequently at startup for faster removal
    removeWatermark();
    setTimeout(removeWatermark, 100);
    setTimeout(removeWatermark, 500);
    setTimeout(removeWatermark, 1000);
    
    // Then run periodically but less frequently
    const interval = setInterval(removeWatermark, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      // D'abord essayer de récupérer depuis localStorage
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (e) {
          console.error('Error parsing stored user data:', e);
        }
      }
      
      // Puis vérifier avec l'API
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData)); // Synchroniser localStorage
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData)); // Stocker les données utilisateur
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setUser(userData);
      
      toast.success(`Bienvenue ${userData.first_name} !`);
      return true;
    } catch (error) {
      toast.error("Email ou mot de passe incorrect");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user"); // Nettoyer les données utilisateur
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    toast.success("Déconnexion réussie");
  };

  const initSampleData = async () => {
    try {
      await axios.post(`${API}/init-sample-data`);
      toast.success("Données de test initialisées");
    } catch (error) {
      console.error("Error initializing sample data:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, initSampleData }}>
      <div className="App font-sans">
        <BrowserRouter>
          {!user ? (
            <Routes>
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Login />} />
            </Routes>
          ) : (
            <div className="flex h-screen bg-gray-50">
              <Sidebar user={user} logout={logout} />
              <div className="flex-1 overflow-auto">
                <Routes>
                  <Route 
                    path="/" 
                    element={
                      user?.role === 'telepro' || user?.role === 'confirmateur' || user?.role === 'superviseur'
                        ? <Navigate to="/prospects" replace /> 
                        : <Dashboard />
                    } 
                  />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/simulator" element={<SimulatorPage />} />
                  <Route path="/contracts" element={<ContractsPage />} />
                  {/* SUNLIFE - Nouveaux modules */}
                  <Route path="/projets" element={<ProjetsPage user={user} />} />
                  <Route path="/produits" element={<ProduitsPage user={user} />} />
                  <Route path="/sav" element={<SAVPage user={user} />} />
                  <Route path="/creneaux-optimises" element={<CreneauxOptimisesPage />} />
                  <Route path="/dossiers-aides" element={<DossiersAidesPage user={user} />} />
                  <Route path="/stock" element={<StockPage user={user} />} />
                  {/* Fin nouveaux modules */}
                  <Route path="/devis-factures" element={<DevisFacturesPage />} />
                  <Route path="/treasury" element={<TreasuryPage />} />
                  <Route path="/my-commissions" element={<MyCommissionsPage />} />
                  <Route path="/prospects" element={<ProspectsPage />} />
                  <Route path="/planning" element={<PlanningPage />} />
                  <Route path="/distribution-rdv" element={<DistributionRdvPage />} />
                  <Route path="/statistiques" element={<StatistiquesPage />} />
                  <Route path="/audit-logs" element={<AuditLogsPage />} />
                  {/* Team Management - accessible uniquement pour Régie et Admin */}
                  {(user?.role === 'Régie' || user?.role === 'admin') && (
                    <Route path="/team-management" element={<TeamManagement />} />
                  )}
                  {/* Signature BDC - accessible uniquement pour commercial, admin, super_admin, administratif */}
                  {(user?.role === 'commercial' || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'administratif') && (
                    <Route path="/signature-bdc" element={<SignatureBDCPage />} />
                  )}
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="*" element={
                    user?.role === 'telepro' || user?.role === 'confirmateur' || user?.role === 'superviseur'
                      ? <Navigate to="/prospects" replace />
                      : <Navigate to="/" replace />
                  } />
                </Routes>
              </div>
            </div>
          )}
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

export default App;